import React from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import WelcomeCard from './WelcomeCard';
import { useUser } from './UserContext';

const MediaSectionCard = ({ headline, text, paragraphs = [], mediaUrl, darkMediaUrl, mediaType = 'video', index }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const currentMediaUrl = (isDark && darkMediaUrl) ? darkMediaUrl : mediaUrl;

  const isLeftSection = index === 1 || index === 3;

  return (
    <Paper
      elevation={3}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: 6,
        marginBottom: '40px',
        width: '100%',
        maxWidth: '1200px',
      }}
    >
      {/* Content Section */}
      <Box
        sx={{
          flex: 1,
          textAlign: 'left',
          margin: { xs: '16px 0', sm: 0 },
          order: isLeftSection ? 2 : 'unset',
        }}
      >
        <Typography variant="h5" gutterBottom>
          {headline}
        </Typography>
        <Typography variant="body1">{text}</Typography>
        {paragraphs.map((p, i) => (
          <Typography key={i} variant="body1" sx={{ mt: 1 }}>{p}</Typography>
        ))}
      </Box>

      {/* Media Section */}
      <Box sx={{
        order: isLeftSection ? 1 : 'unset',
        padding: 2,
      }}>
        {mediaType === 'video' ? (
          <video
            src={currentMediaUrl}
            autoPlay
            loop
            muted
            style={{
              maxWidth: '400px',
              maxHeight: '300px',
              width: '100%',
              height: 'auto',
              borderRadius: '4px'
            }}
          />
        ) : (
          <img
            src={currentMediaUrl}
            alt={headline}
            style={{
              maxWidth: '400px',
              maxHeight: '300px',
              width: '100%',
              height: 'auto',
              borderRadius: '4px'
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

const IntroPage = () => {
  const { user } = useUser();

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      flex: 1,
      paddingTop: '50px',
      paddingBottom: '50px',
    }}>
      <div style={{
        position: 'absolute',
        left: '0px',
        top: '0px',
        width: '100%',
        opacity: '10%',
        pointerEvents: 'none'
      }}>
        <img src='/mascot.png' alt=''/>
      </div>

      {!user && (
        <WelcomeCard />
      )}

        <MediaSectionCard
          headline="Simple Contest Creation"
          text="Snapperish makes contest creation a breeze. With an intuitive interface, users can easily upload and manage their photos for contests. The process is simple and user-friendly, allowing clubs and organizations to showcase their creativity effortlessly."
          mediaUrl="/intro1-light.png"
          darkMediaUrl="/intro1.png"
          mediaType="image"
          index={0}
        />

        <MediaSectionCard
          headline="Robust Contest Management"
          text="Take control of your photo contests with Snapperish's comprehensive contest management features. Set start and end dates for contests, control voting periods, and define upload and voting limits for club members. The app ensures a seamless experience for both club owners and contest administrators, allowing for a customized and organized approach to every contest."
          mediaUrl="/intro2-light.png"
          darkMediaUrl="/intro2.png"
          mediaType="image"
          index={1}
        />

        <MediaSectionCard
          headline="Advanced Security and Randomization"
          text="At Snapperish, we prioritize the security and fairness of photo contests. With special security features and randomization algorithms, each contestant gets a unique and unbiased voting experience. Contestants can't see the number of votes for a photo, ensuring a level playing field. Admins have access to additional information, maintaining transparency without compromising the integrity of the contests."
          mediaUrl="/intro3-light.png"
          darkMediaUrl="/intro3.png"
          mediaType="image"
          index={2}
        />

        <MediaSectionCard
          headline="Affordable Pricing"
          text="Snapperish offers a budget-friendly pricing model at just $8 per month (currently just $5/mth - save $3!) for 25 or fewer club members. Subscriptions can be limited to your contest period, providing flexibility and eliminating the need for permanent subscriptions. Enjoy the full range of Snapperish features without breaking the bank."
          paragraphs={["We also offer a free tier for clubs with up to 5 members — no subscription required."]}
          mediaUrl="/intro5-free-light.png"
          darkMediaUrl="/intro5-free.png"
          mediaType="image"
          index={3}
        />
    </Box>
  );
};

export default IntroPage;